import React, { useState, useEffect } from "react";
import { Coins, Gamepad2, ChevronDown } from "lucide-react";
import { apiRequest } from "../utils/api";

const TABS = ["By Game", "By Coins"];

// Medal colours for rank 1/2/3
const RANK_STYLES = [
  {
    label: "#1",
    textColor: "text-yellow-400",
    shadow: "drop-shadow-[0_0_15px_rgba(250,204,21,0.7)]",
    border: "border-yellow-400/30",
    glow: "shadow-[0_0_30px_rgba(250,204,21,0.15)]",
  },
  {
    label: "#2",
    textColor: "text-slate-300",
    shadow: "drop-shadow-[0_0_10px_rgba(209,213,219,0.5)]",
    border: "border-slate-300/20",
    glow: "shadow-[0_0_20px_rgba(209,213,219,0.08)]",
  },
  {
    label: "#3",
    textColor: "text-amber-600",
    shadow: "drop-shadow-[0_0_10px_rgba(217,119,6,0.5)]",
    border: "border-amber-600/20",
    glow: "shadow-[0_0_20px_rgba(217,119,6,0.08)]",
  },
];

// ── Shared top-3 podium card ──────────────────────────────────────────────────
const PodiumCard = ({ player, rankStyle, valueLabel, value }) => (
  <div
    className={`glass-panel p-8 text-center space-y-4 relative flex flex-col items-center border ${rankStyle.border} ${rankStyle.glow} rounded-3xl hover:scale-[1.02] transition-transform duration-300`}
  >
    <div className={`text-4xl font-black italic tracking-tighter ${rankStyle.textColor} ${rankStyle.shadow}`}>
      {rankStyle.label}
    </div>

    {/* Avatar */}
    <div className="w-24 h-24 bg-game-dark rounded-full flex items-center justify-center overflow-hidden border border-game-border shadow-inner">
      {player.avatarUrl ? (
        <img src={player.avatarUrl} alt={player.gamerName} className="w-full h-full object-cover" />
      ) : (
        <div className="text-3xl grayscale opacity-40">🎮</div>
      )}
    </div>

    <h4 className="font-heading font-black text-xl text-white uppercase tracking-tight">
      {player.gamerName}
    </h4>

    <div className="bg-game-dark px-4 py-2 rounded-lg border border-game-border w-full">
      <p className="text-game-neon font-mono font-bold text-lg">
        {value.toLocaleString()}{" "}
        <span className="text-xs text-game-muted">{valueLabel}</span>
      </p>
    </div>
  </div>
);

// ── Rankings table row ────────────────────────────────────────────────────────
const RankRow = ({ player, valueLabel, value }) => (
  <tr className="hover:bg-game-dark/50 transition-colors group">
    <td className="p-5 text-game-accent font-black tracking-tighter text-lg group-hover:text-game-neon transition-colors">
      #{player.rank}
    </td>
    <td className="p-5">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-game-dark border border-game-border overflow-hidden flex items-center justify-center shrink-0">
          {player.avatarUrl ? (
            <img src={player.avatarUrl} alt={player.gamerName} className="w-full h-full object-cover" />
          ) : (
            <span className="text-xs opacity-30">🎮</span>
          )}
        </div>
        <span className="font-bold text-white uppercase tracking-wide">{player.gamerName}</span>
      </div>
    </td>
    <td className="p-5 text-right">
      <span className="font-mono font-bold text-game-muted group-hover:text-white transition-colors">
        {value.toLocaleString()}
      </span>{" "}
      <span className="text-[10px] text-game-accent uppercase hidden sm:inline">{valueLabel}</span>
    </td>
  </tr>
);

// ── Skeleton loader ───────────────────────────────────────────────────────────
const SkeletonPodium = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
    {[...Array(3)].map((_, i) => (
      <div key={i} className="glass-panel p-8 rounded-3xl border border-game-border animate-pulse flex flex-col items-center gap-4">
        <div className="w-12 h-6 bg-game-dark/60 rounded" />
        <div className="w-24 h-24 bg-game-dark/60 rounded-full" />
        <div className="w-32 h-4 bg-game-dark/60 rounded" />
        <div className="w-full h-10 bg-game-dark/60 rounded-lg" />
      </div>
    ))}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const Leaderboard = () => {
  const [activeTab, setActiveTab] = useState("By Game");

  // By Game state
  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [selectedGameId, setSelectedGameId] = useState("");
  const [gameLeaderboard, setGameLeaderboard] = useState(null);
  const [gameLoading, setGameLoading] = useState(false);
  const [gameError, setGameError] = useState(null);

  // By Coins state
  const [coinLeaderboard, setCoinLeaderboard] = useState(null);
  const [coinLoading, setCoinLoading] = useState(false);
  const [coinError, setCoinError] = useState(null);

  // ── Fetch all games for dropdown ─────────────────────────────────────────
  useEffect(() => {
    const fetchGames = async () => {
      try {
        setGamesLoading(true);
        const res = await apiRequest("/Games");
        if (!res.ok) throw new Error("Failed to load games");
        const data = await res.json();
        setGames(data);
      } catch {
        setGames([]);
      } finally {
        setGamesLoading(false);
      }
    };
    fetchGames();
  }, []);

  // ── Fetch game leaderboard when a game is selected ───────────────────────
  useEffect(() => {
    if (!selectedGameId) return;
    const fetchGameLeaderboard = async () => {
      try {
        setGameLoading(true);
        setGameError(null);
        const res = await apiRequest(
          `/Players/leaderboard?tab=ByGame&gameId=${selectedGameId}`
        );
        if (!res.ok) throw new Error("Failed to load leaderboard");
        const data = await res.json();
        setGameLeaderboard(data);
      } catch {
        setGameError("Could not load leaderboard. Please try again.");
        setGameLeaderboard(null);
      } finally {
        setGameLoading(false);
      }
    };
    fetchGameLeaderboard();
  }, [selectedGameId]);

  // ── Fetch coin leaderboard when tab is active ─────────────────────────────
  useEffect(() => {
    if (activeTab !== "By Coins") return;
    if (coinLeaderboard) return; // already fetched
    const fetchCoinLeaderboard = async () => {
      try {
        setCoinLoading(true);
        setCoinError(null);
        const res = await apiRequest("/Players/leaderboard/coins");
        if (!res.ok) throw new Error("Failed to load coin leaderboard");
        const data = await res.json();
        setCoinLeaderboard(data);
      } catch {
        setCoinError("Could not load coin leaderboard. Please try again.");
      } finally {
        setCoinLoading(false);
      }
    };
    fetchCoinLeaderboard();
  }, [activeTab, coinLeaderboard]);

  // ── Render leaderboard content ────────────────────────────────────────────
  const renderLeaderboardContent = ({ topThree, rankings, valueKey, valueLabel, loading, error }) => {
    if (loading) return <SkeletonPodium />;

    if (error) return (
      <div className="text-center py-20 text-game-muted">
        <p className="text-sm font-bold uppercase tracking-widest">{error}</p>
      </div>
    );

    if (!topThree) return null;

    return (
      <>
        {/* Top 3 Podium */}
        {topThree.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
            {topThree.map((player, i) => (
              <PodiumCard
                key={player.playerProfileId}
                player={player}
                rankStyle={RANK_STYLES[i]}
                valueLabel={valueLabel}
                value={player[valueKey] ?? 0}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-10 text-game-muted">
            <p className="text-sm font-bold uppercase tracking-widest">No data yet</p>
          </div>
        )}

        {/* Full Rankings Table */}
        {rankings.length > 0 && (
          <div className="overflow-x-auto glass-panel rounded-2xl border border-game-border mt-8 p-1 shadow-inner">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-game-dark/80 text-game-muted uppercase tracking-widest text-xs font-black border-b border-game-border">
                  <th className="p-5 text-left w-20">Rank</th>
                  <th className="p-5 text-left">Player</th>
                  <th className="p-5 text-right">{valueLabel}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-game-border/50">
                {rankings.map((player) => (
                  <RankRow
                    key={player.playerProfileId}
                    player={player}
                    valueLabel={valueLabel}
                    value={player[valueKey] ?? 0}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="space-y-8 p-4 md:p-6 animate-in fade-in duration-500 max-w-6xl mx-auto">
      {/* Header */}
      <h2 className="text-4xl font-heading font-black italic tracking-tighter uppercase text-transparent bg-clip-text bg-gradient-to-r from-game-neon to-game-accent drop-shadow-[0_0_10px_rgba(255,184,0,0.3)]">
        Leaderboard
      </h2>

      {/* Tab Navigation */}
      <div className="flex gap-3">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-2 px-6 py-2.5 border rounded-xl uppercase tracking-widest text-xs font-bold transition-all duration-300 ${
              activeTab === tab
                ? "bg-gradient-to-r from-game-accent to-game-neon text-white border-transparent shadow-[0_0_15px_rgba(255,184,0,0.4)] scale-[1.02]"
                : "text-game-muted border-game-border bg-game-dark/50 hover:text-white hover:border-game-muted/50"
            }`}
          >
            {tab === "By Game" ? <Gamepad2 size={13} /> : <Coins size={13} />}
            {tab}
          </button>
        ))}
      </div>

      {/* ── BY GAME TAB ───────────────────────────────────────────────────── */}
      {activeTab === "By Game" && (
        <div className="space-y-6">
          {/* Game Dropdown */}
          <div className="relative w-full max-w-sm">
            <label className="block text-[10px] font-black uppercase tracking-widest text-game-muted mb-2">
              Select Game
            </label>
            <div className="relative">
              <select
                value={selectedGameId}
                onChange={(e) => setSelectedGameId(e.target.value)}
                disabled={gamesLoading}
                className="w-full appearance-none bg-game-dark border border-game-border text-white text-sm font-bold uppercase tracking-wide px-4 py-3 pr-10 rounded-xl focus:outline-none focus:border-game-neon transition-colors cursor-pointer disabled:opacity-50"
              >
                <option value="">
                  {gamesLoading ? "Loading games..." : "— Choose a game —"}
                </option>
                {games.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.title}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={16}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-game-muted pointer-events-none"
              />
            </div>
          </div>

          {/* Game Leaderboard */}
          {!selectedGameId ? (
            <div className="text-center py-20 text-game-muted flex flex-col items-center gap-3">
              <Gamepad2 size={48} className="opacity-20" />
              <p className="text-sm font-bold uppercase tracking-widest">
                Select a game to view its leaderboard
              </p>
            </div>
          ) : (
            renderLeaderboardContent({
              topThree: gameLeaderboard?.topThree ?? null,
              rankings: gameLeaderboard?.rankings ?? [],
              valueKey: "score",
              valueLabel: "PTS",
              loading: gameLoading,
              error: gameError,
            })
          )}
        </div>
      )}

      {/* ── BY COINS TAB ──────────────────────────────────────────────────── */}
      {activeTab === "By Coins" &&
        renderLeaderboardContent({
          topThree: coinLeaderboard?.topThree ?? null,
          rankings: coinLeaderboard?.rankings ?? [],
          valueKey: "coinBalance",
          valueLabel: "Coins",
          loading: coinLoading,
          error: coinError,
        })}
    </div>
  );
};

export default Leaderboard;